/**
 * CopyRunSummaryButton - copy a run's ticket, PR links and total cost in one click
 */

import { useEffect, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { copyRichText } from '../../lib/clipboard';
import { buildRunSummary, type RunSummaryInput } from '../../lib/run-summary';

const COPIED_FEEDBACK_MS = 2000;

type CopyState = 'idle' | 'copied' | 'failed';

const LABELS: Record<CopyState, string> = {
  idle: 'Copy ticket, PR and cost',
  copied: 'Copied',
  failed: 'Copy failed',
};

export function CopyRunSummaryButton(props: RunSummaryInput) {
  const [state, setState] = useState<CopyState>('idle');

  useEffect(() => {
    if (state === 'idle') return;
    const timer = setTimeout(() => setState('idle'), COPIED_FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [state]);

  const handleCopy = async () => {
    try {
      await copyRichText(buildRunSummary(props));
      setState('copied');
    } catch (error) {
      console.error('Failed to copy run summary:', error);
      setState('failed');
    }
  };

  const label = LABELS[state];
  const Icon = state === 'copied' ? Check : Copy;

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={label}
      title={label}
      className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
    >
      <Icon
        className={`w-4 h-4 ${state === 'copied' ? 'text-green-600 dark:text-green-400' : ''}`}
      />
    </button>
  );
}
